import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { List, X, ArrowRight, LockKey } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";

const NAV = [
  { to: "/about-us",      label: "About" },
  { to: "/product",       label: "Product" },
  { to: "/capabilities",  label: "Features" },
  { to: "/how-it-works",  label: "How it works" },
  { to: "/workflow",      label: "Workflow" },
  { to: "/resources",     label: "Resources" },
];

/**
 * White marketing header — clean, modern, enterprise-first pattern.
 *   - Left:   SRES logo (S-hexagon + wordmark) → clickable to landing
 *   - Centre: About · Product · Features · How it works · Workflow · Resources
 *   - Right:  Login (outlined blue) + Open the Studio (solid blue)
 */
export default function MarketingHeader() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();

  const RIGHT_CTA = user ? (
    <button
      onClick={() => nav("/app")}
      className="inline-flex items-center gap-2 h-11 px-6 rounded-lg bg-brand-blue hover:bg-brand-blueDeep text-white text-[15px] font-semibold transition-colors shadow-[0_1px_2px_rgba(15,23,42,0.08)]"
      data-testid="marketing-open-workspace"
    >
      Open the Studio <ArrowRight size={16} weight="bold" />
    </button>
  ) : (
    <>
      <Link
        to="/login"
        className="inline-flex items-center gap-2 h-11 px-6 rounded-lg border-2 border-brand-blue bg-white text-brand-blue hover:bg-brand-blue hover:text-white text-[15px] font-semibold transition-colors"
        data-testid="marketing-login-link"
      >
        <LockKey size={15} weight="bold" /> Login
      </Link>
      <Link
        to="/signup"
        className="inline-flex items-center gap-2 h-11 px-6 rounded-lg bg-brand-blue hover:bg-brand-blueDeep text-white text-[15px] font-semibold transition-colors shadow-[0_1px_2px_rgba(15,23,42,0.08)]"
        data-testid="marketing-signup-cta"
      >
        Open the Studio <ArrowRight size={16} weight="bold" />
      </Link>
    </>
  );

  return (
    <header
      className="sticky top-0 z-40 w-full bg-white/95 backdrop-blur border-b border-slate-200"
      data-testid="marketing-header"
    >
      <div className="max-w-[1400px] mx-auto h-[76px] px-6 md:px-10 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="marketing-header-brand">
          <img
            src="/sres-logo-horizontal.png?v=5"
            alt="SRES · Shanvi Resource Estimation Studio"
            className="h-11 md:h-12 w-auto object-contain"
            draggable={false}
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden xl:flex items-center gap-9" data-testid="marketing-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                `text-[15px] font-medium transition-colors ${
                  isActive
                    ? "text-brand-blue"
                    : "text-slate-800 hover:text-brand-blue"
                }`
              }
              data-testid={`marketing-nav-${n.to.replace("/", "")}`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        {/* Right CTAs */}
        <div className="hidden xl:flex items-center gap-3 shrink-0">{RIGHT_CTA}</div>

        {/* Mobile toggle */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="xl:hidden w-10 h-10 flex items-center justify-center rounded-md text-slate-700 hover:bg-slate-100"
          aria-label="Toggle menu"
          data-testid="marketing-menu-toggle"
        >
          {open ? <X size={20} weight="bold" /> : <List size={20} weight="bold" />}
        </button>
      </div>

      {/* Mobile panel */}
      {open ? (
        <div className="xl:hidden border-t border-slate-200 bg-white" data-testid="marketing-mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-3">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `text-sm font-medium py-2 ${isActive ? "text-brand-blue" : "text-slate-700"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <div className="pt-3 mt-2 border-t border-slate-200 flex flex-col gap-2">
              {user ? (
                <Link
                  to="/app"
                  onClick={() => setOpen(false)}
                  className="inline-flex items-center justify-center h-11 rounded-md bg-brand-blue text-white text-sm font-semibold"
                >
                  Open the Studio
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setOpen(false)}
                    className="inline-flex items-center justify-center h-11 rounded-md border-2 border-brand-blue text-brand-blue text-sm font-semibold"
                  >
                    Login
                  </Link>
                  <Link
                    to="/signup"
                    onClick={() => setOpen(false)}
                    className="inline-flex items-center justify-center h-11 rounded-md bg-brand-blue text-white text-sm font-semibold"
                  >
                    Open the Studio
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </header>
  );
}
